import {
  SanityProductFragment,
  SanityProductOption,
  SanityProductVariantFragment,
} from '@data/sanity/queries/types/product'
import { Locale } from './language'
import { Filter, FilterValue, getFormattedProductTitle } from './product'
import { OptionValue } from './product-options'
import { shopifyColorOptionNames } from './shopify/product'

/**
 * Gets default product option taking into account filters.
 */
export const getDefaultOptionsWithFilters = (
  product: SanityProductFragment,
  activeFilterValues: FilterValue[],
): OptionValue[] => {
  return activeFilterValues
    .map((filterValue) => {
      const currentFilter = product.filters?.find(
        (filter) =>
          !!filter.forOption &&
          filter.filter?.slug?.current === filterValue.value,
      )

      if (!currentFilter) {
        return null
      }

      const option = currentFilter.forOption.split(':')

      return {
        name: option[0],
        value: option[1],
      } satisfies OptionValue
    })
    .filter(Boolean) as OptionValue[]
}

/**
 * Gets the default product variant based on option values.
 */
const getDefaultProductVariant = (
  product: SanityProductFragment,
  optionValues: OptionValue[],
) => {
  const lastOptionValue =
    optionValues.length > 0 ? optionValues[optionValues.length - 1] : null

  const firstOption = product.options?.[0]
  const firstOptionValue: OptionValue | null = firstOption
    ? {
        name: firstOption?.name,
        value: firstOption?.values?.[0],
      }
    : null

  const optionValue = lastOptionValue ?? firstOptionValue

  if (!optionValue) {
    return null
  }

  return (
    product.variants?.find((variant) => {
      return variant.options.some((option) => {
        return (
          option.name.toLowerCase() === optionValue.name.toLowerCase() &&
          option.value.toLowerCase() === optionValue.value.toLowerCase()
        )
      })
    }) ?? null
  )
}

/**
 * Gets filter values from filters.
 */
const getFilterValues = (filters: Filter[]): FilterValue[] => {
  return (
    filters.flatMap((filter) =>
      filter.values.map((value) => ({
        name: filter.name,
        value,
      })),
    ) ?? []
  )
}

/**
 * Gets the filtered, default or first product variant based on current active filters.
 */
export const getFilteredProductVariant = (
  product: SanityProductFragment,
  filters: Filter[],
  preferredOption?: OptionValue,
) => {
  const activeFilterValues = getFilterValues(filters)
  const defaultOptions = getDefaultOptionsWithFilters(
    product,
    activeFilterValues,
  )
  const defaultVariant = getDefaultProductVariant(product, defaultOptions)
  const firstVariant = product.variants?.[0] ?? null
  const preferredOptionVariant = preferredOption
    ? product.variants?.find((variant) => {
        return variant.options.some((option) => {
          return (
            option.name.toLowerCase() === preferredOption.name.toLowerCase() &&
            option.value.toLowerCase() === preferredOption.value.toLowerCase()
          )
        })
      })
    : null

  return preferredOptionVariant ?? defaultVariant ?? firstVariant
}

/**
 * Gets product card title based on preferred option.
 */
export const getProductTitle = (
  locale: Locale,
  product: SanityProductFragment,
  variant?: SanityProductVariantFragment,
  preferredOption?: OptionValue,
) => {
  const titleParts: string[] = [getFormattedProductTitle(locale, product.title)]
  const preferredOptionTitle = preferredOption
    ? variant?.options?.find((option) => {
        return option.name.toLowerCase() === preferredOption.name.toLowerCase()
      })?.value
    : null
  const colorOption = product.options.find((option) => {
    return shopifyColorOptionNames.includes(option.name)
  })
  const colorOptionValues = colorOption?.values ?? []

  // Add color to title if preferred option is given and product has more than 1 color
  if (preferredOptionTitle && colorOptionValues.length > 1) {
    titleParts.push(preferredOptionTitle)
  }

  return titleParts.join(' ')
}

/**
 * Gets product option that should be surfaced.
 */
export const getSurfacedOption = (
  productOptions: SanityProductOption[],
  surfaceOption?: string,
) => {
  if (!productOptions || !surfaceOption) {
    return
  }

  const surfacedOptionPosition = parseInt(surfaceOption)

  return productOptions.find((option) => {
    return (
      option.position === surfacedOptionPosition && option.values.length > 1
    )
  })
}
